import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  Paper,
  Stack,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  SmartToy as AgentIcon,
  ErrorOutline as ErrorIcon,
  CheckCircle as SuccessIcon,
  Schedule as TimeIcon,
  Refresh as RefreshIcon,
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Info as InfoIcon,
  Key as KeyIcon,
  Token as TokenIcon,
  DataUsage as DataIcon,
} from '@mui/icons-material';
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { motion } from 'framer-motion';
import { externalApiService } from '../services/externalApiService';

const ApiKeyDashboard = ({ open, onClose, apiKey }) => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);

  const periodOptions = [
    { value: 7, label: 'Últimos 7 dias' },
    { value: 30, label: 'Últimos 30 dias' },
    { value: 90, label: 'Últimos 90 dias' },
    { value: 365, label: 'Último ano' },
  ];

  const loadStats = async () => {
    if (!apiKey?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await externalApiService.getApiKeyStats(apiKey.id, period);
      setStats(response);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
      setError('Erro ao carregar estatísticas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && apiKey?.id) {
      loadStats();
    }
  }, [open, apiKey?.id, period]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 4,
    }).format(value || 0);
  };

  const formatTime = (ms) => {
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.round(ms)}ms`;
  };

  // Cores para gráficos
  const colors = {
    primary: '#1976d2',
    success: '#4caf50',
    error: '#f44336',
    warning: '#ff9800',
    info: '#00bcd4',
  };

  // Dados para gráfico de pizza (status)
  const statusData = stats?.breakdown?.by_status ? [
    { name: 'Sucesso', value: stats.breakdown.by_status.success || 0, color: colors.success },
    { name: 'Erro Cliente', value: (stats.breakdown.by_status.bad_request || 0) + (stats.breakdown.by_status.unauthorized || 0) + (stats.breakdown.by_status.forbidden || 0), color: colors.warning },
    { name: 'Rate Limit', value: stats.breakdown.by_status.rate_limit || 0, color: colors.error },
    { name: 'Erro Servidor', value: stats.breakdown.by_status.server_error || 0, color: colors.error },
  ].filter(item => item.value > 0) : [];

  // Dados para gráfico de linha (por dia)
  const dailyData = stats?.breakdown?.by_day ? Object.entries(stats.breakdown.by_day)
    .sort(([a], [b]) => new Date(a) - new Date(b))
    .map(([date, data]) => ({
      date: new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
      requests: data.requests || 0,
      successful: data.successful || 0,
      failed: data.failed || 0,
      avg_response_time: data.avg_response_time || 0,
    })) : [];

  // Dados para gráfico de barras (por agente)
  const agentData = stats?.breakdown?.by_agent ? Object.entries(stats.breakdown.by_agent)
    .sort(([, a], [, b]) => b.requests - a.requests)
    .slice(0, 5)
    .map(([agent, data]) => ({
      agent,
      requests: data.requests || 0,
      tokens: data.total_tokens || 0,
      avg_response_time: data.avg_response_time || 0,
    })) : [];

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <AnalyticsIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Analytics Dashboard
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {apiKey?.key_name}
                </Typography>
                <Chip 
                  label={apiKey?.key_preview} 
                  variant="outlined" 
                  size="small"
                  sx={{ 
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    height: 24
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                displayEmpty
                sx={{ fontSize: '0.875rem' }}
              >
                {periodOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Atualizar dados">
              <IconButton onClick={loadStats} disabled={loading} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} sx={{ ml: 1 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : stats ? (
          <Box sx={{ pb: 2 }}>
            {/* Métricas Principais */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <AssessmentIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Métricas do Período
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                {/* Total de Requests */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Avatar sx={{ 
                      bgcolor: 'primary.light', 
                      mx: 'auto', 
                      mb: 2,
                      width: 56,
                      height: 56
                    }}>
                      <TrendingUpIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 700, 
                      mb: 1,
                      color: 'primary.main'
                    }}>
                      {formatNumber(stats.period_stats?.total_requests)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Total de Requests
                    </Typography>
                  </Box>
                </Grid>

                {/* Taxa de Sucesso */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Avatar sx={{ 
                      bgcolor: 'success.light', 
                      mx: 'auto', 
                      mb: 2,
                      width: 56,
                      height: 56
                    }}>
                      <SuccessIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 700, 
                      mb: 1,
                      color: 'success.main'
                    }}>
                      {(100 - (stats.period_stats?.error_rate || 0)).toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                      Taxa de Sucesso
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={100 - (stats.period_stats?.error_rate || 0)}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: 'success.lighter',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'success.main'
                        }
                      }}
                    />
                  </Box>
                </Grid>

                {/* Tempo de Resposta */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Avatar sx={{ 
                      bgcolor: 'info.light', 
                      mx: 'auto', 
                      mb: 2,
                      width: 56,
                      height: 56
                    }}>
                      <SpeedIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 700, 
                      mb: 1,
                      color: 'info.main'
                    }}>
                      {formatTime(stats.period_stats?.avg_response_time)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Tempo Médio de Resposta
                    </Typography>
                  </Box>
                </Grid>

                {/* Tokens Processados */}
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Avatar sx={{ 
                      bgcolor: 'warning.light', 
                      mx: 'auto', 
                      mb: 2,
                      width: 56,
                      height: 56
                    }}>
                      <TokenIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 700, 
                      mb: 1,
                      color: 'warning.main'
                    }}>
                      {formatNumber(stats.period_stats?.total_tokens)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Tokens Processados
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Estatísticas Adicionais */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                      <PeopleIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {formatNumber(stats.period_stats?.unique_users)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Usuários Únicos
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}>
                      <AgentIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                        {formatNumber(stats.period_stats?.unique_agents)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Agentes Utilizados
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            {/* Análises Detalhadas */}
            <Grid container spacing={3}>
              {/* Timeline de Requests */}
              {dailyData.length > 0 && (
                <Grid item xs={12} lg={8}>
                  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimelineIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Timeline de Atividade
                        </Typography>
                        <Chip 
                          label={`${Math.min(dailyData.length, 7)} dias`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <CardContent>
                      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {dailyData.slice(0, 7).map((day, index) => (
                          <Box key={index} sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            py: 1,
                            px: 2,
                            mb: 1,
                            backgroundColor: index % 2 === 0 ? 'grey.50' : 'transparent',
                            borderRadius: 1
                          }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {day.date}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary">Total</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {day.requests}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" color="success.main">Sucesso</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                  {day.successful}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" color="error.main">Falhas</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                  {day.failed}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary">Tempo Médio</Typography>
                                <Typography variant="body2">
                                  {formatTime(day.avg_response_time)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Paper>
                </Grid>
              )}

              {/* Distribuição por Status */}
              {statusData.length > 0 && (
                <Grid item xs={12} lg={4}>
                  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                    <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PieChartIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Distribuição por Status
                        </Typography>
                      </Box>
                    </Box>
                    <CardContent>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {statusData.map((status, index) => {
                          const total = statusData.reduce((sum, item) => sum + item.value, 0);
                          const percentage = total > 0 ? (status.value / total * 100).toFixed(1) : 0;
                          return (
                            <Box key={index}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box 
                                    sx={{ 
                                      width: 12, 
                                      height: 12, 
                                      borderRadius: '50%', 
                                      backgroundColor: status.color 
                                    }} 
                                  />
                                  <Typography variant="body2">{status.name}</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {status.value}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {percentage}%
                                  </Typography>
                                </Box>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={Number(percentage)}
                                sx={{ 
                                  height: 6, 
                                  borderRadius: 3,
                                  backgroundColor: 'grey.200',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: status.color
                                  }
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </CardContent>
                  </Paper>
                </Grid>
              )}

              {/* Top Agentes */}
              {agentData.length > 0 && (
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BarChartIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Agentes Mais Utilizados
                        </Typography>
                        <Chip 
                          label={`Top ${agentData.length}`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <CardContent>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {agentData.map((agent, index) => {
                          const maxRequests = Math.max(...agentData.map(a => a.requests));
                          const percentage = maxRequests > 0 ? (agent.requests / maxRequests * 100) : 0;
                          return (
                            <Box key={index}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                    {agent.agent}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatTime(agent.avg_response_time)} médio • {formatNumber(agent.tokens)} tokens
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {formatNumber(agent.requests)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    requests
                                  </Typography>
                                </Box>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={percentage}
                                sx={{ 
                                  height: 8, 
                                  borderRadius: 4,
                                  backgroundColor: 'grey.200',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: colors.primary
                                  }
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </CardContent>
                  </Paper>
                </Grid>
              )}

              {/* Erros Principais */}
              {stats?.breakdown?.top_errors?.length > 0 && (
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ErrorIcon color="error" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Principais Erros
                        </Typography>
                        <Chip 
                          label={`${period} dias`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <CardContent>
                      {stats.breakdown.top_errors.map((error, index) => (
                        <Box key={index} sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          py: 1,
                          borderBottom: index < stats.breakdown.top_errors.length - 1 ? '1px solid' : 'none',
                          borderBottomColor: 'divider'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ErrorIcon color="error" fontSize="small" />
                            <Typography variant="body2">
                              {error.error_type || 'Erro desconhecido'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {error.error_message?.substring(0, 50)}...
                            </Typography>
                          </Box>
                          <Chip 
                            label={error.count} 
                            size="small" 
                            color="error" 
                            variant="outlined"
                          />
                        </Box>
                      ))}
                    </CardContent>
                  </Paper>
                </Grid>
              )}

              {/* Informações da API Key */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <KeyIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Detalhes da API Key
                      </Typography>
                    </Box>
                  </Box>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Nome:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {stats.api_key_info?.key_name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Status:</Typography>
                        <Chip 
                          label={stats.api_key_info?.is_active ? 'Ativa' : 'Inativa'}
                          color={stats.api_key_info?.is_active ? 'success' : 'error'}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Criada em:</Typography>
                        <Typography variant="body1">
                          {new Date(stats.api_key_info?.created_at).toLocaleDateString('pt-BR')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Último uso:</Typography>
                        <Typography variant="body1">
                          {stats.api_key_info?.last_used_at 
                            ? new Date(stats.api_key_info.last_used_at).toLocaleDateString('pt-BR')
                            : 'Nunca usado'
                          }
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Agentes Permitidos:</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                          {stats.api_key_info?.allowed_agents?.length > 0 ? 
                            stats.api_key_info.allowed_agents.map((agent, index) => (
                              <Chip key={index} label={agent} size="small" variant="outlined" />
                            )) : 
                            <Chip label="Todos os agentes" size="small" />
                          }
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Canais Permitidos:</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                          {stats.api_key_info?.allowed_channels?.map((channel, index) => (
                            <Chip key={index} label={channel} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              Nenhum dado disponível para o período selecionado
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiKeyDashboard;
